import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Plus, Filter, Download, Eye, Edit, Search, Building2, Trash2, X, Save, CheckCircle2, AlertCircle, MapPin } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from '../components/ui/Tooltip';
const initialStudiesData = Array.from({
  length: 50
}, (_, i) => ({
  id: i + 1,
  name: i % 2 === 0 ? `Studio Commerciale ${i}` : `Studio Lavoro ${i}`,
  type: i % 2 === 0 ? 'Commercialista' : 'Consulente del Lavoro',
  city: ['Arezzo', 'Firenze', 'Siena', 'Perugia', 'Prato', 'Sansepolcro'][i % 6],
  email: `info@studio${i}.it`,
  phone: `0575 ${200000 + i}`,
  via: 'Via Roma',
  civico: `${10 + i}`,
  comune: ['Arezzo', 'Firenze', 'Siena', 'Perugia', 'Prato', 'Sansepolcro'][i % 6],
  cap: '52100',
  provincia: ['AR', 'FI', 'SI', 'PG', 'PO', 'AR'][i % 6]
}));
const columnsConfig = [{
  key: 'name',
  label: 'Ragione sociale'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'city',
  label: 'Città'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'phone',
  label: 'Telefono'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function StudiesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialStudiesData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['name', 'email']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleView = (item: any) => {
    setSelectedItem(item);
    setIsViewModalOpen(true);
  };
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    setFormData({});
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setSelectedItem(item);
    setIsDeleteConfirmOpen(true);
  };
  const confirmDelete = () => {
    setDeleteSuccess(true);
    setTimeout(() => {
      setDeleteSuccess(false);
      setIsDeleteConfirmOpen(false);
      addNotification('success', 'Studio eliminato', 'Lo studio è stato eliminato con successo.');
    }, 1500);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Studio salvato', 'Lo studio è stato salvato con successo.');
    }, 2000);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  // Get unique cities for filter
  const uniqueCities = Array.from(new Set(initialStudiesData.map(item => item.city))).sort();
  return <Layout data-id="element-4718">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-4719">
        <div data-id="element-4720">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-4721">
            Anagrafica studi
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-4722">
            Gestione partner esterni (Commercialisti, Consulenti del Lavoro).
          </p>
        </div>
        <div className="flex gap-3" data-id="element-4723">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-4725" />} data-id="element-4724">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-4727" />} data-id="element-4726">
            Nuovo studio
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-4728">
        <div className="p-4 flex flex-col gap-4" data-id="element-4729">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-4730">
            <div className="flex-1" data-id="element-4731">
              <Input placeholder="Cerca studio..." startContent={<Search className="h-4 w-4" data-id="element-4733" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-4732" />
            </div>
            <div className="flex gap-2" data-id="element-4734">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-4736" />} data-id="element-4735">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-4737">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="studies" data-id="element-4738" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-4739">
              <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Commercialista',
            label: 'Commercialista'
          }, {
            value: 'Consulente del Lavoro',
            label: 'Consulente del Lavoro'
          }, {
            value: 'Altro',
            label: 'Altro'
          }]} data-id="element-4740" />
              <Select label="Città" value={filters.city || ''} onChange={e => setFilter('city', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, ...uniqueCities.map(city => ({
            value: city,
            label: city
          }))]} data-id="element-4741" />
              <div className="col-span-full flex justify-end" data-id="element-4742">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-4744" />} data-id="element-4743">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-4745">
        <div className="overflow-x-auto" data-id="element-4746">
          <table className="w-full text-left border-collapse" data-id="element-4747">
            <thead data-id="element-4748">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-4749">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-left w-32' : ''}`} data-id="element-4750">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-4751">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-4752">
                  {visibleColumns.includes('name') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-4753">
                      {item.name}
                    </td>}
                  {visibleColumns.includes('type') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-4754">
                      {item.type}
                    </td>}
                  {visibleColumns.includes('city') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-4755">
                      {item.city}
                    </td>}
                  {visibleColumns.includes('email') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-4756">
                      {item.email}
                    </td>}
                  {visibleColumns.includes('phone') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-4757">
                      {item.phone}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-6 py-4" data-id="element-4758">
                      <div className="flex items-center gap-1" data-id="element-4759">
                        <Tooltip content="Visualizza studio" data-id="element-4760">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleView(item)} data-id="element-4761">
                            <Eye className="h-4 w-4 text-slate-500" data-id="element-4762" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Modifica studio" data-id="element-4763">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleEdit(item)} data-id="element-4764">
                            <Edit className="h-4 w-4 text-seform-orange" data-id="element-4765" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Elimina studio" data-id="element-4766">
                          <Button variant="light" size="sm" className="!px-1" onClick={() => handleDelete(item)} data-id="element-4767">
                            <Trash2 className="h-4 w-4 text-red-500" data-id="element-4768" />
                          </Button>
                        </Tooltip>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-4769">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-4770" />
        </div>
      </Card>

      {/* View Modal (Consultation) */}
      <Modal isOpen={isViewModalOpen} onClose={() => setIsViewModalOpen(false)} title="Dettaglio studio" size="lg" data-id="element-4771">
        {selectedItem && <div className="space-y-6" data-id="element-4772">
            <div className="grid grid-cols-2 gap-4" data-id="element-4773">
              <div data-id="element-4774">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4775">
                  Ragione sociale
                </span>
                <p className="font-medium" data-id="element-4776">{selectedItem.name}</p>
              </div>
              <div data-id="element-4777">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4778">
                  Tipologia
                </span>
                <p className="font-medium" data-id="element-4779">{selectedItem.type}</p>
              </div>
              <div data-id="element-4780">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4781">
                  Città
                </span>
                <p className="font-medium" data-id="element-4782">{selectedItem.city}</p>
              </div>
              <div data-id="element-4783">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4784">
                  Email
                </span>
                <p className="font-medium" data-id="element-4785">{selectedItem.email}</p>
              </div>
              <div data-id="element-4786">
                <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4787">
                  Telefono
                </span>
                <p className="font-medium" data-id="element-4788">{selectedItem.phone}</p>
              </div>
            </div>

            <div className="h-px bg-slate-100" data-id="element-4789"></div>

            <section data-id="element-4790">
              <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4791">
                <MapPin className="h-4 w-4" data-id="element-4792" /> Indirizzo
              </h4>
              <div className="grid grid-cols-2 gap-4" data-id="element-4793">
                <div data-id="element-4794">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4795">
                    Via
                  </span>
                  <p className="font-medium" data-id="element-4796">{selectedItem.via}</p>
                </div>
                <div data-id="element-4797">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4798">
                    Civico
                  </span>
                  <p className="font-medium" data-id="element-4799">{selectedItem.civico}</p>
                </div>
                <div data-id="element-4800">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4801">
                    Comune
                  </span>
                  <p className="font-medium" data-id="element-4802">{selectedItem.comune}</p>
                </div>
                <div data-id="element-4803">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4804">
                    CAP
                  </span>
                  <p className="font-medium" data-id="element-4805">{selectedItem.cap}</p>
                </div>
                <div data-id="element-4806">
                  <span className="text-xs font-semibold text-slate-400 uppercase" data-id="element-4807">
                    Provincia
                  </span>
                  <p className="font-medium" data-id="element-4808">{selectedItem.provincia}</p>
                </div>
              </div>
            </section>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4809">
              <Button variant="flat" color="default" onClick={() => setIsViewModalOpen(false)} data-id="element-4810">
                Chiudi
              </Button>
              <Button onClick={() => {
            setIsViewModalOpen(false);
            handleEdit(selectedItem);
          }} startContent={<Edit className="h-4 w-4" data-id="element-4812" />} data-id="element-4811">
                Modifica
              </Button>
            </div>
          </div>}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica studio' : 'Nuovo studio'} size="2xl" data-id="element-4813">
        <AnimatePresence mode="wait" data-id="element-4814">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-16" data-id="element-4815">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4816">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-4817" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4818">
                Studio salvato con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4819">
              <div className="space-y-8" data-id="element-4820">
                <section data-id="element-4821">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4822">
                    <Building2 className="h-4 w-4" data-id="element-4823" /> Dettagli studio
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4824">
                    <Input label="Ragione sociale" value={formData.name || ''} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} required data-id="element-4825" />
                    <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                  ...formData,
                  type: e.target.value
                })} options={[{
                  value: 'Commercialista',
                  label: 'Commercialista'
                }, {
                  value: 'Consulente del Lavoro',
                  label: 'Consulente del Lavoro'
                }, {
                  value: 'Altro',
                  label: 'Altro'
                }]} required data-id="element-4826" />
                    <Input label="Email" type="email" value={formData.email || ''} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} data-id="element-4827" />
                    <Input label="Telefono" type="tel" value={formData.phone || ''} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} data-id="element-4828" />
                  </div>
                </section>

                <div className="h-px bg-slate-100" data-id="element-4829"></div>

                <section data-id="element-4830">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-4831">
                    <MapPin className="h-4 w-4" data-id="element-4832" /> Indirizzo
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-6" data-id="element-4833">
                    <div className="md:col-span-4" data-id="element-4834">
                      <Input label="Via" value={formData.via || ''} onChange={e => setFormData({
                    ...formData,
                    via: e.target.value
                  })} data-id="element-4835" />
                    </div>
                    <div className="md:col-span-2" data-id="element-4836">
                      <Input label="Civico" value={formData.civico || ''} onChange={e => setFormData({
                    ...formData,
                    civico: e.target.value
                  })} data-id="element-4837" />
                    </div>
                    <div className="md:col-span-3" data-id="element-4838">
                      <Input label="Comune" value={formData.comune || ''} onChange={e => setFormData({
                    ...formData,
                    comune: e.target.value
                  })} data-id="element-4839" />
                    </div>
                    <div className="md:col-span-2" data-id="element-4840">
                      <Input label="CAP" value={formData.cap || ''} onChange={e => setFormData({
                    ...formData,
                    cap: e.target.value
                  })} data-id="element-4841" />
                    </div>
                    <div className="md:col-span-1" data-id="element-4842">
                      <Input label="Prov." value={formData.provincia || ''} onChange={e => setFormData({
                    ...formData,
                    provincia: e.target.value
                  })} maxLength={2} data-id="element-4843" />
                    </div>
                  </div>
                </section>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-4844">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-4845">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-4847" />} data-id="element-4846">
                    Salva studio
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Conferma eliminazione" size="sm" data-id="element-4848">
        <AnimatePresence mode="wait" data-id="element-4849">
          {deleteSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.3
        }} className="flex flex-col items-center justify-center py-12" data-id="element-4850">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            delay: 0.1
          }} className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-4851">
                <CheckCircle2 className="w-10 h-10 text-green-500" data-id="element-4852" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-4853">
                Eliminato con successo
              </p>
            </motion.div> : <motion.div key="confirm" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-4854">
              <div className="space-y-6" data-id="element-4855">
                <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex gap-3" data-id="element-4856">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-4857" />
                  <p className="text-sm text-red-700" data-id="element-4858">
                    Attenzione: questa azione è irreversibile. Lo studio verrà
                    eliminato definitivamente.
                  </p>
                </div>
                <div className="flex justify-end gap-3 pt-4" data-id="element-4859">
                  <Button variant="flat" color="default" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-4860">
                    Annulla
                  </Button>
                  <Button color="danger" onClick={confirmDelete} data-id="element-4861">
                    Elimina
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>
    </Layout>;
}